import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import OtpForm from '@/components/Auth/OtpForm';
import LoginForm from '@/components/Auth/LoginForm';
import RegisterForm from '@/components/Auth/RegisterForm';
import BrandLogo from '@/components/brand/BrandLogo';
import './get-started.css';

export default function SignInPage() {
  const [mode, setMode] = useState('password');
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect') || '';
  const roleParam = searchParams.get('role') || '';

  // Intelligent role detection:
  // 1. Explicit ?role=doctor or ?role=clinician
  // 2. Redirect URL contains /oncoagent
  // 3. Previously saved preference in localStorage
  // 4. Default: 'patient'
  const isClinicianRedirect =
    roleParam === 'doctor' ||
    roleParam === 'clinician' ||
    redirectParam.includes('oncoagent');

  const [role, setRole] = useState(() => {
    if (isClinicianRedirect) return 'doctor';
    try {
      const saved = localStorage.getItem('byonco_auth_role');
      if (saved === 'doctor' || saved === 'patient') return saved;
    } catch (_) {}
    return 'patient';
  });

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    try {
      localStorage.setItem('byonco_auth_role', newRole);
      localStorage.setItem('byonco_user_role', newRole);
    } catch (_) {}
  };

  const isDoctor = role === 'doctor';

  const railCopy = isDoctor
    ? mode === 'register'
      ? {
          title: 'Create your clinician account',
          body: 'Join the OncoAgent oncology network. Streamline patient referrals, manage consult SLAs, and coordinate specialist care.',
          foot: 'Built for doctors and clinicians. HIPAA-ready. Evidence-backed oncology routing.',
        }
      : mode === 'otp'
        ? {
            title: 'Clinician sign-in without password',
            body: 'We will send a secure verification code to your verified medical or clinic email.',
            foot: 'Built for doctors and clinicians. HIPAA-ready. Evidence-backed oncology routing.',
          }
        : {
            title: 'Clinical oncology navigation',
            body: 'Access OncoAgent referral workspaces, triage patient records, and coordinate specialist handoffs.',
            foot: 'Built for doctors and clinicians. HIPAA-ready. Evidence-backed oncology routing.',
          }
    : mode === 'register'
      ? {
          title: 'Create your companion account',
          body: 'One place for care notes, visit prep, and the questions that get clearer answers sooner.',
          foot: 'Built for patients and families. Private by design. Ready when you are.',
        }
      : mode === 'otp'
        ? {
            title: 'Sign in without a password',
            body: 'We will email a one-time code. Fast for families who need to get back to care.',
            foot: 'Built for patients and families. Private by design. Ready when you are.',
          }
        : {
            title: 'Your care companion is waiting',
            body: 'Sign in to continue conversations, records, and personalized next steps.',
            foot: 'Built for patients and families. Private by design. Ready when you are.',
          };

  const cardSubtitle = isDoctor
    ? mode === 'register'
      ? 'Create your clinician account. Built for doctors, oncologists, and healthcare teams.'
      : mode === 'otp'
        ? 'Sign in to your clinical workspace. Built for doctors and healthcare teams.'
        : 'Sign in to your clinical workspace. Built for doctors, oncologists, and healthcare teams.'
    : mode === 'register'
      ? 'Create your companion account. Built for patients and families.'
      : mode === 'otp'
        ? 'Sign in with a one-time code. Built for patients and families.'
        : 'Sign in to your companion. Built for patients and families.';

  return (
    <div className="auth-shell">
      <div className="auth-shell-inner">
        <aside className="auth-rail" aria-label="ByOnco sign in">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <p className="auth-rail-brand">byonco</p>
              {isDoctor && (
                <span className="text-[11px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">
                  OncoAgent
                </span>
              )}
            </div>
            <p className="auth-rail-lede">{railCopy.title}</p>
            <p className="auth-rail-lede" style={{ marginTop: '0.75rem' }}>
              {railCopy.body}
            </p>
          </div>
          <p className="auth-rail-foot">{railCopy.foot}</p>
        </aside>

        <main className="auth-main">
          <div className="auth-main-card">
            <div className="auth-main-top text-center sm:text-left">
              <BrandLogo size="md" className="justify-center sm:justify-start text-forest-ink" />
              <p className="mt-3 text-[15px] text-[#3d4a42]">
                {cardSubtitle}
              </p>
            </div>

            {/* Role & Persona Selector Box */}
            <div className="auth-role-box">
              <div className="auth-role-header">
                <span className="auth-role-kicker">I am accessing ByOnco as</span>
                <span className={`auth-role-badge ${isDoctor ? 'is-doctor' : 'is-patient'}`}>
                  {isDoctor ? '🩺 Clinician Portal' : '🌿 Patient & Family'}
                </span>
              </div>

              {/* Native Dropdown Selector for full accessibility & mobile */}
              <div className="auth-role-select-wrap">
                <span className="auth-role-select-icon" aria-hidden="true">
                  {isDoctor ? '🩺' : '🌿'}
                </span>
                <select
                  id="auth-role-select"
                  aria-label="Select account type: Patient or Clinician"
                  value={role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="auth-role-select"
                >
                  <option value="patient">🌿 Patients & Families (Care Companion)</option>
                  <option value="doctor">🩺 Doctors & Clinicians (OncoAgent Portal)</option>
                </select>
                <div className="auth-role-select-chevron" aria-hidden="true">
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path
                      d="M1.5 1.75L6 6.25L10.5 1.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Fast-switch segmented pills */}
              <div className="auth-role-pills" role="tablist" aria-label="Switch account type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isDoctor}
                  className={`auth-role-pill ${!isDoctor ? 'is-active' : ''}`}
                  onClick={() => handleRoleChange('patient')}
                >
                  <span className="auth-role-pill-icon" aria-hidden="true">🌿</span>
                  <span className="auth-role-pill-label">Patients & Families</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isDoctor}
                  className={`auth-role-pill ${isDoctor ? 'is-active' : ''}`}
                  onClick={() => handleRoleChange('doctor')}
                >
                  <span className="auth-role-pill-icon" aria-hidden="true">🩺</span>
                  <span className="auth-role-pill-label">Doctors & Clinicians</span>
                  <span className="auth-role-pill-tag">OncoAgent</span>
                </button>
              </div>

              {/* Dynamic Contextual Helper Note */}
              <div className="auth-role-note">
                {isDoctor ? (
                  <>
                    <span>Clinical triage, oncology referrals, & consult SLA tracking.</span>
                    <Link to="/oncoagent" className="auth-role-note-link">
                      OncoAgent overview ↗
                    </Link>
                  </>
                ) : (
                  <span>Care notes, oncology visits, records, & second opinions.</span>
                )}
              </div>
            </div>

            {mode === 'otp' ? (
              <OtpForm
                role={role}
                onUsePassword={() => setMode('password')}
              />
            ) : null}

            {mode === 'password' ? (
              <LoginForm
                role={role}
                onSwitchToRegister={() => setMode('register')}
                onForgotPassword={() => {
                  window.location.href = '/reset-password';
                }}
              />
            ) : null}

            {mode === 'register' ? (
              <RegisterForm
                role={role}
                onSwitchToLogin={() => setMode('password')}
              />
            ) : null}

            <div className="auth-switch">
              {mode === 'otp' ? (
                <button type="button" onClick={() => setMode('password')}>
                  Use email and password instead
                </button>
              ) : (
                <button type="button" onClick={() => setMode('otp')}>
                  Use a one-time code
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
